// interview-answer-directive-processing.ts — B4 (plan 20260915-1650 phase-04).
//
// Đường đi CÓ THẬT cho câu trả lời của người: inbox directive `answer` →
// `interview.json`.
//
// Vì sao phải có module riêng thay vì dùng đường `answer` sẵn có: đo verbatim
// 2026-09-16 cho thấy đường cũ NUỐT câu trả lời.
// `supervisor-directive-processing.ts:26` xếp `answer` chung
// `REDIRECT_LIKE_KINDS` rồi đưa cho `applyRedirectDirective`, mà
// `RedirectDirectivePayload` chỉ đọc `proposed_changes` /
// `supersede_done_ticket_ids` / `invalidate_gate_ids` — KHÔNG field nào nhận
// `text`. Directive vẫn được ghi bền và đánh dấu `.processed`, nên nhìn từ
// ngoài mọi thứ "xanh" trong khi nội dung trả lời đã mất.
//
// Module này KHÔNG đụng `redirect`/`revert` (hai kind kia đi đường cũ, đang
// chạy đúng) — chỉ nhận `answer` mang `question_id`. Payload `answer` kiểu cũ
// (`{ticket, request_id, text}`) bị TỪ CHỐI CÓ LÝ DO chứ không im lặng bỏ qua:
// im lặng chính là lỗi gốc.
//
// V2-0 (spec V2 §4.0): mỗi answer áp được ghi thêm event journal
// `decision_answered` TRƯỚC `markDirectiveProcessed`, idempotent theo
// `directive_id` (decision-answered-journal-event.ts).

import { randomUUID } from "node:crypto";
import {
  decisionAnsweredDelta,
  readJournaledDecisionDirectiveIds,
} from "./decision-answered-journal-event.js";
import {
  isDirectiveProcessed,
  listInboxDirectiveIds,
  markDirectiveProcessed,
  readDirective,
} from "./directive-inbox.js";
import { readInterview, recordInterviewAnswer } from "./interview-store.js";
import type { MissionStore } from "./mission-store.js";

export interface ProcessInterviewAnswerDirectivesInput {
  controlRoot: string;
  /** Journal của mission — nơi ghi `decision_answered` (V2-0). */
  store: MissionStore;
  now?: () => Date;
}

export interface RejectedAnswerDirective {
  directiveId: string;
  reason: string;
}

export interface ProcessInterviewAnswerDirectivesResult {
  appliedDirectiveIds: string[];
  rejected: RejectedAnswerDirective[];
}

/** `question_id` + giá trị (`text` hoặc `value`) — hình dạng payload B4 nhận. */
function readAnswerPayload(
  payload: Record<string, unknown>,
): { questionId: string; value: string | string[] } | undefined {
  const questionId = payload.question_id;
  if (typeof questionId !== "string" || questionId.length === 0) return undefined;

  const raw = payload.value ?? payload.text;
  if (typeof raw === "string" && raw.trim().length > 0) {
    return { questionId, value: raw.trim() };
  }
  if (Array.isArray(raw) && raw.every((v) => typeof v === "string" && v.length > 0)) {
    return { questionId, value: raw as string[] };
  }
  return undefined;
}

/**
 * Quét inbox, áp mọi directive `answer` chưa xử lý vào interview store.
 *
 * Gọi ở TICK BOUNDARY (cùng chỗ `processPendingRedirectDirectives` chạy) —
 * không bao giờ giữa attempt. Idempotent bằng `.processed` marker của
 * directive-inbox, y như mọi directive khác: chạy lại tick sau không áp lại.
 *
 * Directive bị từ chối VẪN được đánh dấu processed: giữ nó "chưa xử lý" sẽ
 * khiến mọi tick sau thử lại cùng một payload hỏng và sinh cùng một lỗi mãi
 * mãi. Lý do từ chối được TRẢ VỀ cho caller để báo người, không nuốt.
 */
export async function processPendingInterviewAnswerDirectives(
  input: ProcessInterviewAnswerDirectivesInput,
): Promise<ProcessInterviewAnswerDirectivesResult> {
  const appliedDirectiveIds: string[] = [];
  const rejected: RejectedAnswerDirective[] = [];

  // Mission chưa hỏi câu nào ⇒ không có gì để trả lời. Thoát sớm, KHÔNG đánh
  // dấu processed: câu hỏi có thể được ghi ở tick sau, và khi đó directive
  // vẫn còn nguyên để áp.
  if ((await readInterview(input.controlRoot)) === undefined) {
    return { appliedDirectiveIds, rejected };
  }

  // Quét journal tối đa MỘT lần mỗi lượt, chỉ khi thật có answer cần áp.
  let journaled: Set<string> | undefined;

  for (const directiveId of await listInboxDirectiveIds(input.controlRoot)) {
    const directive = await readDirective(input.controlRoot, directiveId);
    if (directive === undefined) continue;
    if (directive.kind !== "answer") continue;
    if (await isDirectiveProcessed(input.controlRoot, directiveId)) continue;

    const parsed = readAnswerPayload(directive.payload);
    if (parsed === undefined) {
      rejected.push({
        directiveId,
        reason:
          `directive answer thiếu "question_id" hoặc giá trị rỗng — dùng ` +
          "`@avatar answer <question-id>: <giá trị>`.",
      });
      await markDirectiveProcessed(input.controlRoot, directiveId);
      continue;
    }

    // Crash sau khi journal nhưng trước marker ⇒ answer + event đã bền; chỉ
    // còn thiếu marker. Không ghi lại answer (giữ `answered_at`), không event thứ hai.
    journaled ??= await readJournaledDecisionDirectiveIds(input.controlRoot);
    if (journaled.has(directiveId)) {
      await markDirectiveProcessed(input.controlRoot, directiveId);
      appliedDirectiveIds.push(directiveId);
      continue;
    }

    const recorded = await recordInterviewAnswer({
      controlRoot: input.controlRoot,
      questionId: parsed.questionId,
      answer: parsed.value,
      answeredBy: directive.actor_id,
      now: input.now,
    });
    if (!recorded.ok) {
      rejected.push({ directiveId, reason: `"${parsed.questionId}": ${recorded.reason}` });
      await markDirectiveProcessed(input.controlRoot, directiveId);
      continue;
    }

    const interview = await readInterview(input.controlRoot);
    const question = interview?.questions.find((q) => q.id === parsed.questionId);
    if (question !== undefined) {
      const answeredAt = interview?.answers[parsed.questionId]?.answered_at;
      await input.store.commit([
        {
          event_id: randomUUID(),
          delta: decisionAnsweredDelta(
            question,
            parsed.value,
            `user:${directive.actor_id}`,
            directiveId,
            answeredAt,
          ),
        },
      ]);
    }
    await markDirectiveProcessed(input.controlRoot, directiveId);
    appliedDirectiveIds.push(directiveId);
  }

  return { appliedDirectiveIds, rejected };
}
